import {
  Box,
  Typography,
  Grid,
  IconButton,
  Paper,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  PermIdentity,
  VideoCameraBackOutlined,
  CardGiftcardOutlined,
  EditNoteOutlined,
  HomeOutlined,
} from "@mui/icons-material";
import { ConfirmationProps } from "../../../types/steps";
import { ServiceType, ServiceTypes } from "../../../types/service-type";
import formatDate from "../../../utils/FormatDate";
import { useState } from "react";

const SectionCard = ({
  title,
  icon,
  children,
  stepIndex,
  goToStep,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  stepIndex?: number;
  goToStep?: (stepIndex: number) => void;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${colors.primary[800]}`,
        boxShadow: 0,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
        {goToStep && stepIndex !== undefined && (
          <IconButton onClick={() => goToStep(stepIndex)}>
            <EditNoteOutlined />
          </IconButton>
        )}
      </Box>
      {children}
    </Paper>
  );
};

const InfoGrid = ({
  data,
  columns = 3,
}: {
  data: { [key: string]: string | number | boolean | null | undefined };
  columns?: number;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Grid container spacing={2}>
      {Object.entries(data).map(([key, value]) => {
        const displayValue =
          value === null || value === undefined || value === ""
            ? "N/A"
            : String(value);

        return (
          <Grid size={{ xs: 12, sm: 6, md: 12 / columns }} key={key}>
            <Typography variant="body2" sx={{ color: colors.primary[300] }}>
              {key}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {displayValue}
            </Typography>
          </Grid>
        );
      })}
    </Grid>
  );
};

const AddBookingConfirmation: React.FC<ConfirmationProps> = ({
  serviceType,
  bookingData,
  goToStep,
}): React.ReactElement => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  let clientInformation: {
    [key: string]: string | number | boolean | null | undefined;
  } = {};
  let serviceInformation: {
    [key: string]: string | number | null | undefined;
  } = {};
  let conferenceInformation: {
    [key: string]: string | number | null | undefined;
  } = {};
  let livestreamInformation: {
    [key: string]: string | number | boolean | null | undefined;
  } = {};
  let venueInformation: {
    [key: string]: string | number | boolean | null | undefined;
  } = {};

  const servicesConfig: ServiceTypes = ServiceType;

  if (serviceType === "FUNERAL") {
    clientInformation = {
      Name: bookingData.first_name || "N/A",
      Surname: bookingData.surname || "N/A",
      Email: bookingData.email || "N/A",
      Phone: bookingData.phone_numbers || bookingData.phone || "N/A",
      "Relationship To Deceased": bookingData.relationship_to_deceased || "N/A",
      "Source of booking": bookingData.source_of_booking || "N/A",
      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.service_notes || "N/A",
    };
    serviceInformation = {
      "Deceased Name": `${
        bookingData.deceased_list?.[0]?.deceased_name || "N/A"
      } ${bookingData.deceased_list?.[0]?.deceased_surname || "N/A"}`,
      "Deceased Second Name":
        bookingData.deceased_list?.[0]?.second_name || "N/A",
      Salutation: bookingData.deceased_list?.[0]?.deceased_salutation || "N/A",
      Nickname: bookingData.deceased_list?.[0]?.nickname || "N/A",
      "Date of Birth":
        formatDate(bookingData.deceased_list?.[0]?.date_of_birth) || "N/A",
      "Date of Death":
        formatDate(bookingData.deceased_list?.[0]?.date_of_death) || "N/A",
      Religion: bookingData.religion || "N/A",
      Denomination: bookingData.denomination || "N/A",
      "Type of Service": bookingData.type_of_service || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      Date: formatDate(bookingData.date_of_service) || "N/A",
      "Venue Name": bookingData.name || bookingData.venue_name || "N/A",
      "Address One": bookingData.address_one || "N/A",
      "Address Two": bookingData.address_two || "N/A",
      Suburb: bookingData.suburb || "N/A",
      City: bookingData.city || "N/A",
      "Name of Priest": bookingData.preacher_name || "N/A",
      "Name of MC": bookingData.mc_name || "N/A",
      "Name of Eulogist": bookingData.eulogist_name || "N/A",
    };
  } else if (serviceType === "CHURCH") {
    clientInformation = {
      Source: bookingData.source_of_booking || "N/A",
      "First Name": bookingData.first_name || "N/A",
      Surname: bookingData.surname || "N/A",
      Email: bookingData.email || "N/A",
      Phone: bookingData.phone || "N/A",

      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.service_notes || "N/A",
    };
    serviceInformation = {
      "Name of Church": bookingData.church_name || "N/A",
      "Leader Preacher Name": bookingData.leader_preacher_name || "N/A",
      "Leader Preacher Surname": bookingData.leader_preacher_surname || "N/A",
      Title: bookingData.title || "N/A",
      "Preacher Name": bookingData.preacher_name || "N/A",
      "Preacher Surname": bookingData.preacher_surname || "N/A",
      "Date of Service": formatDate(bookingData.date_of_service) || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      "Church Phone": bookingData.phone || "N/A",
      "Church Email": bookingData.email || "N/A",
      "Venue Name": bookingData.name || bookingData.venue_name || "N/A",
      "Address One": bookingData.address_one || "N/A",
      "Address Two": bookingData.address_two || "N/A",
      Suburb: bookingData.suburb || "N/A",
      City: bookingData.city || "N/A",
    };
  } else if (serviceType === "OTHER") {
    clientInformation = {
      "First Name": bookingData.first_name || "N/A",
      Surname: bookingData.surname || "N/A",
      Email: bookingData.email || "N/A",
      Phone: bookingData.phone || "N/A",
      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.notes || "N/A",
      Source: bookingData.source || "N/A",
    };
    serviceInformation = {
      "Type of Content": bookingData.content_type || "N/A",
      Title: bookingData.title || "N/A",
      "Sub Title": bookingData.sub_title || "N/A",
      "Host Name": bookingData.host || "N/A",
      "Host Surname": bookingData.host_surname || "N/A",
      "Guest Name": bookingData.guest || "N/A",
      "Guest Surname": bookingData.guest_surname || "N/A",
      "Date of Service": formatDate(bookingData.date_of_service) || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      "Venue Name": bookingData.address_two || "N/A",
      "Service Address": bookingData.address_one || "N/A",
      City: bookingData.city || "N/A",
    };
  } else if (serviceType === "FUNERAL_HOME") {
    clientInformation = {
      Source: bookingData.source_of_booking || "N/A",
      Company: bookingData.company_name || "N/A",
      // "Branch Name": bookingData.branch_name || "N/A",
      // "Branch Representative": bookingData.branch_rep || "N/A",
      "Representative Email": bookingData.email || "N/A",
      "Representative Phone": bookingData.phone || "N/A",
      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.service_notes || "N/A",
    };
    serviceInformation = {
      "Deceased Name": `${
        bookingData.deceased_list?.[0]?.deceased_name || "N/A"
      } ${bookingData.deceased_list?.[0]?.deceased_surname || "N/A"}`,
      Salutation: bookingData.deceased_list?.[0]?.deceased_salutation || "N/A",
      Nickname: bookingData.deceased_list?.[0]?.nickname || "N/A",
      Religion: bookingData.religion || "N/A",
      Denomination: bookingData.denomination || "N/A",
      "Type of Service": bookingData.type_of_service || "N/A",
      "Date of Birth":
        formatDate(bookingData.deceased_list?.[0]?.date_of_birth) || "N/A",
      "Date of Death":
        formatDate(bookingData.deceased_list?.[0]?.date_of_death) || "N/A",
      "Date of Service": formatDate(bookingData.date_of_service) || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      "Venue Name": bookingData.address_two || "N/A",
      "Service Address": bookingData.address_one || "N/A",
      City: bookingData.city || "N/A",
    };
  } else if (serviceType === "EVENT") {
    clientInformation = {
      Source: bookingData.source || "N/A",
      "Branch Name": bookingData.branch_name || "N/A",
      "Branch Representative": bookingData.branch_rep || "N/A",
      "Representative Email": bookingData.representative_email || "N/A",
      "Representative Phone": bookingData.representative_phone || "N/A",
      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.service_notes || "N/A",
    };
    serviceInformation = {
      "Event Name": bookingData.event_name || "N/A",
      "Expected Audience": bookingData.expected_audience_size || "N/A",
      "Guest of Honour": bookingData.guest_of_honour || "N/A",
      "Type of Service": bookingData.type_of_service || "N/A",
      "Date of Service": formatDate(bookingData.date_of_service) || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      "Venue Name": bookingData.address_two || "N/A",
      "Venue Address": bookingData.address_one || "N/A",
      "Venue City": bookingData.city || "N/A",
    };
  } else if (serviceType === "CONFERENCE") {
    clientInformation = {
      Source: bookingData.source_of_booking || "N/A",
      "Client Name": bookingData.first_name || "N/A",
      "Client Surname": bookingData.surname || "N/A",
      Email: bookingData.email || "N/A",
      "Phone Number": bookingData.phone || "N/A",
      "Special Request": bookingData.special_request || "N/A",
      Notes: bookingData.service_notes || "N/A",
    };
    conferenceInformation = {
      "Conference Name": bookingData.title || "N/A",
      "Start Date": formatDate(bookingData.start_date_of_event) || "N/A",
      "End Date": formatDate(bookingData.end_date_of_event) || "N/A",
      "Start Time": bookingData.start_time || "N/A",
      "End Time": bookingData.end_time || "N/A",
      "Expected Audience": bookingData.expected_audience_size || "N/A",
      "Access Type": bookingData.access_type || "N/A",
      "Platform requirement": bookingData.enable_livestreaming ? "Yes" : "No",
      "Streaming Platform": bookingData.streaming_platform || "N/A",
      "Sessions to stream": bookingData.session_to_stream || "N/A",
      "Record Session": bookingData.record_stream ? "Yes" : "No",
      "On Demand Access": bookingData.provide_on_demand_access ? "Yes" : "No",
      "Retention Duration": bookingData.retention_duration || "N/A",
      "Is Branding Needed ?": bookingData.require_custom_branding
        ? "Yes"
        : "No",
      "Branding Details": bookingData.branding_requirements || "N/A",

      "Venue Name": bookingData.name || bookingData.venue_name || "N/A",
      "Address One": bookingData.address_one || "N/A",
      "Address Two": bookingData.address_two || "N/A",
      Suburb: bookingData.suburb || "N/A",
      City: bookingData.city || "N/A",
    };
  }

  const packageInformation = {
    Package: bookingData.package?.name || "N/A",
    "Package ID": bookingData.package?.id || "N/A",
    "Additional Services":
      bookingData.additional_services
        ?.map((service) => service.label || service.value)
        .join(", ") || "None",
    "Package Price": `$${bookingData.package?.price?.toFixed(2) ?? 0}`,
    "Total Amount": `$${bookingData.total?.toFixed(2) ?? 0}`,
  };

  const deceasedImageUrl =
    (serviceType === "FUNERAL" && bookingData.deceased_list?.[0]?.photo_url) ||
    (serviceType === "FUNERAL_HOME" &&
      bookingData.deceased_list?.[0]?.photo_url);

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <SectionCard
            title="Organiser’s Information"
            icon={<PermIdentity />}
            stepIndex={0}
            goToStep={goToStep}
          >
            <InfoGrid data={clientInformation} columns={3} />
          </SectionCard>
        </Grid>
        {serviceType !== "CONFERENCE" && (
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionCard
              title="Services Information"
              icon={<VideoCameraBackOutlined />}
              stepIndex={1}
              goToStep={goToStep}
            >
              {deceasedImageUrl ? (
                <Box>
                  <Box>
                    <img
                      src={deceasedImageUrl}
                      alt="Deceased"
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "1px solid #ccc",
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                (serviceType === "FUNERAL" ||
                  serviceType === "FUNERAL_HOME") && (
                  <Box mt={2}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.primary[300] }}
                    >
                      Deceased Image
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      No Image Provided
                    </Typography>
                  </Box>
                )
              )}
              <InfoGrid data={serviceInformation} columns={3} />
            </SectionCard>
          </Grid>
        )}
        {serviceType !== "CONFERENCE" && (
          <Grid size={{ xs: 12, md: 4 }}>
            <SectionCard
              title="Package Selection"
              icon={<CardGiftcardOutlined />}
              stepIndex={2}
              goToStep={goToStep}
            >
              <InfoGrid data={packageInformation} columns={1} />
            </SectionCard>
          </Grid>
        )}
        {serviceType === "CONFERENCE" && (
          <>
            <Grid size={{ xs: 12 }}>
              <SectionCard
                title="Conference Information"
                icon={<VideoCameraBackOutlined />}
                stepIndex={1}
                goToStep={goToStep}
              >
                <InfoGrid data={conferenceInformation} columns={3} />
              </SectionCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Package Selection"
                icon={<CardGiftcardOutlined />}
                stepIndex={4}
                goToStep={goToStep}
              >
                <InfoGrid data={packageInformation} columns={1} />
              </SectionCard>
            </Grid>
          </>
        )}
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddBookingConfirmation;
